import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, BellOff, AlertTriangle, Pill, FileEdit, Plus, Inbox, Check, X } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  missed_med: { icon: Pill, color: "text-red-600", bg: "bg-red-50" },
  note_edited: { icon: FileEdit, color: "text-amber-600", bg: "bg-amber-50" },
  emar_edited: { icon: FileEdit, color: "text-amber-600", bg: "bg-amber-50" },
  med_status_change: { icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50" },
  med_request: { icon: Plus, color: "text-blue-600", bg: "bg-blue-50" },
};

export default function AdminNotifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    const [n, r] = await Promise.all([
      api.get("/notifications"),
      api.get("/med-requests"),
    ]);
    setItems(n.data); setRequests(r.data);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
  const markAll = async () => { await api.post("/notifications/read-all"); load(); toast.success("All marked read"); };

  const approve = async (id) => { await api.post(`/med-requests/${id}/approve`); load(); toast.success("Approved & added to client"); };
  const reject = async (id) => { await api.post(`/med-requests/${id}/reject`); load(); toast("Request rejected"); };

  return (
    <div className="space-y-6" data-testid="admin-notifications">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-label">Inbox</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">In-App Notifications</h1>
          <p className="text-sm text-muted-foreground">All compliance events. Emails are intentionally disabled.</p>
        </div>
        <Button variant="outline" onClick={markAll} data-testid="mark-all-read"><BellOff className="w-4 h-4 mr-2" /> Mark all read</Button>
      </div>

      {requests.filter((r) => r.status === "pending").length > 0 && (
        <Card className="p-5 border-l-4 border-l-blue-600 bg-blue-50/40">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-blue-600" />
              <p className="text-label">Pending Medication Requests</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/med-requests")} data-testid="goto-med-requests">
              Open med queue
            </Button>
          </div>
          <div className="space-y-2">
            {requests.filter((r) => r.status === "pending").map((r) => {
              const label = ({
                new: "New",
                discontinue: "Discontinue",
                dose_change: "Dose change",
                frequency_change: "Frequency change",
              }[r.action] || "Change");
              return (
                <div key={r.id} className="bg-white border border-border rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid={`med-request-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">
                      <Badge variant="outline" className="mr-1.5">{label}</Badge>
                      {r.name} — {r.dose}
                      {r.is_prn && <Badge variant="secondary" className="ml-1">PRN</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">Requested by {r.provider_name} · Reason: {r.reason || "—"}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approve(r.id)} data-testid={`approve-${r.id}`}><Check className="w-4 h-4 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(r.id)} className="text-red-600" data-testid={`reject-${r.id}`}><X className="w-4 h-4 mr-1" /> Reject</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card>
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Bell className="w-4 h-4 text-primary" />
          <p className="text-label">All Notifications</p>
          <Badge variant="secondary" className="ml-2">{items.filter((i) => !i.read).length} unread</Badge>
        </div>
        <div className="divide-y divide-border" data-testid="notifications-list">
          {items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No notifications.</p>}
          {items.map((n) => {
            const meta = KIND_META[n.kind] || { icon: Bell, color: "text-muted-foreground", bg: "bg-secondary" };
            const Icon = meta.icon;
            return (
              <button
                key={n.id}
                onClick={() => { markRead(n.id); if (n.link) navigate(n.link); }}
                className={`w-full text-left p-4 flex items-start gap-3 hover:bg-secondary transition-colors ${!n.read ? "bg-blue-50/40" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${meta.bg} ${meta.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">{n.title}</p>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-blue-600" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
